import React, { useState, useEffect } from 'react';
import { Prestataire } from '../../types';

interface PrestataireFormProps {
  prestataire?: Prestataire | null;
  onSubmit: (data: Omit<Prestataire, 'id' | 'created_at' | 'updated_at'>) => void;
  onCancel: () => void;
  loading: boolean;
}

const PrestataireForm: React.FC<PrestataireFormProps> = ({ 
  prestataire, 
  onSubmit, 
  onCancel, 
  loading 
}) => {
  const [formData, setFormData] = useState<Omit<Prestataire, 'id' | 'created_at' | 'updated_at'>>({
    nom: '',
    contact_principal: '',
    telephone: '',
    email: '',
    adresse: '',
    delai_fabrication_moyen: 7,
    specialites: '',
    commentaires: '',
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (prestataire) {
      setFormData({
        nom: prestataire.nom,
        contact_principal: prestataire.contact_principal || '',
        telephone: prestataire.telephone || '',
        email: prestataire.email || '',
        adresse: prestataire.adresse || '',
        delai_fabrication_moyen: prestataire.delai_fabrication_moyen,
        specialites: prestataire.specialites || '',
        commentaires: prestataire.commentaires || '',
        is_active: prestataire.is_active,
      });
    }
  }, [prestataire]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? Number(value) : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du laboratoire est requis';
    }

    if (!formData.contact_principal.trim()) {
      newErrors.contact_principal = 'Le contact principal est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
    }

    if (!formData.adresse.trim()) {
      newErrors.adresse = 'L\'adresse est requise';
    }

    if (formData.delai_fabrication_moyen < 1) {
      newErrors.delai_fabrication_moyen = 'Le délai doit être d\'au moins 1 jour';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">
          {prestataire ? 'Modifier' : 'Ajouter'} un prestataire
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nom du laboratoire */}
          <div>
            <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
              Nom du laboratoire *
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.nom ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Laboratoire Dentaire XYZ"
            />
            {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
          </div>

          {/* Contact principal */}
          <div>
            <label htmlFor="contact_principal" className="block text-sm font-medium text-gray-700 mb-2">
              Contact principal *
            </label>
            <input
              type="text"
              id="contact_principal"
              name="contact_principal"
              value={formData.contact_principal}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.contact_principal ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Dr. Martin Dubois"
            />
            {errors.contact_principal && <p className="mt-1 text-sm text-red-600">{errors.contact_principal}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="contact@laboratoire.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          {/* Téléphone */}
          <div>
            <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone *
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.telephone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="224-123-456-789"
            />
            {errors.telephone && <p className="mt-1 text-sm text-red-600">{errors.telephone}</p>}
          </div>
        </div>

        {/* Adresse */}
        <div>
          <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-2">
            Adresse complète *
          </label>
          <textarea
            id="adresse"
            name="adresse"
            value={formData.adresse}
            onChange={handleChange}
            rows={3}
            className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              errors.adresse ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Adresse complète du laboratoire"
          />
          {errors.adresse && <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Délai de fabrication */}
          <div>
            <label htmlFor="delai_fabrication_moyen" className="block text-sm font-medium text-gray-700 mb-2">
              Délai de fabrication moyen (jours) *
            </label>
            <input
              type="number"
              id="delai_fabrication_moyen"
              name="delai_fabrication_moyen"
              value={formData.delai_fabrication_moyen}
              onChange={handleChange}
              min="1"
              className={`w-full px-3 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.delai_fabrication_moyen ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.delai_fabrication_moyen && <p className="mt-1 text-sm text-red-600">{errors.delai_fabrication_moyen}</p>}
          </div>

          {/* Statut actif */}
          <div className="flex items-center">
            <label htmlFor="is_active" className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Prestataire actif</span>
            </label>
          </div>
        </div>

        {/* Spécialités */}
        <div>
          <label htmlFor="specialites" className="block text-sm font-medium text-gray-700 mb-2">
            Spécialités
          </label>
          <textarea
            id="specialites"
            name="specialites"
            value={formData.specialites}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Ex: Prothèses complètes, partielles, implants, couronnes..."
          />
        </div>

        {/* Commentaires */}
        <div>
          <label htmlFor="commentaires" className="block text-sm font-medium text-gray-700 mb-2">
            Commentaires
          </label>
          <textarea
            id="commentaires"
            name="commentaires"
            value={formData.commentaires}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Commentaires sur le prestataire..."
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Enregistrement...' : (prestataire ? 'Modifier' : 'Ajouter')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PrestataireForm;